/** Auto-generated from docs/landing-secondary-i18n-source-en.md */
export const landingSecondaryEn = {
  help: {
    index: {
      seo: {
        title: 'Help Center | Veeso AI Tutorials & Support',
        description:
          'Get step-by-step tutorials, FAQs, and support for Veeso AI. Learn how to turn content into production-ready designs fast.',
        keywords:
          'Veeso AI help, AI design support, content-first design, tutorials, FAQs, social media design, SMB design tool',
      },
      hero: {
        title: 'Help Center',
        subtitle: 'Find tutorials, FAQs, and support for your AI design journey.',
      },
      category: {
        getting_started: {
          title: 'Getting Started',
          description: 'Learn the basics of Veeso AI and how to create your first design.',
        },
        account_billing: {
          title: 'Account & Billing',
          description: 'Manage your subscription, billing details, and account settings.',
        },
        features_tools: {
          title: 'Features & Tools',
          description:
            'Deep dive into Veeso AI features, from auto-layout to AI content generation.',
        },
        troubleshooting: {
          title: 'Troubleshooting',
          description: 'Solutions to common issues and how to contact support.',
        },
      },
      contact: {
        heading: 'Still need help?',
        cta: 'Contact Support',
      },
      faq: {
        title: 'FAQ',
        items: [
          {
            question: 'What is Veeso AI?',
            answerParagraph1:
              'For SMB owners, marketers, operations, founders with limited design resources and tight deadlines, producing professional visuals is slow and expensive.',
            answerParagraph2:
              'Veeso is a content-first design tool that turns copywriting into clear, polished visuals in one click, one minute, no design skills required.',
            answerParagraph3:
              'Just paste your content or text, and instantly generate beautiful designs for Social Media Post, Poster, Presentation, Carousel, Multi-Page Brochure, Long-Form Content, Restaurant Menu, Invitation Card, Pull Up Banner, Flyer, Album Cover.',
          },
          {
            question: 'How is Veeso different from AI image generation tools?',
            answerParagraph1:
              'Most AI design tools generate images. Veeso generates complete, editable design files with:',
            listItems: [
              'Editable text blocks',
              'Logical content hierarchy',
              'Multi-page formatting',
              'Export-ready designs',
            ],
          },
          {
            question: 'Who is Veeso AI built for?',
            answer:
              'Veeso is designed for creators, solo founders, marketers, and small to mid-sized businesses (SMBs) who need professional designs without hiring a designer or spending hours formatting content.',
          },
          {
            question: 'What is the core Content-First philosophy?',
            answerParagraph1:
              'Content-First means design starts from your information — not from templates or images. Instead of searching for fixed templates and forcing content into them, Veeso analyzes your raw text and automatically structures it into clear sections, headlines, and visual hierarchy. We believe your content should not be locked inside:',
            listItems: ['A specific slide layout', 'A rigid template', 'A single platform format'],
            answerParagraph2:
              'Veeso decouples content from format, allowing the same input to be transformed into:',
            listItems2: ['Presentations', 'Newsletters', 'Social posts', 'Reports', 'Brochures'],
            answerParagraph3: 'One source of content. Multiple design outputs.',
          },
          {
            question: 'What is the Magic Template?',
            answerParagraph1:
              "Magic Template is Veeso AI's style control system. Instead of fixed, static templates, Magic Template dynamically adapts layout structure based on:",
            listItems: [
              'Content length',
              'Information hierarchy',
              'Tone and intent',
              'Output format',
            ],
            answerParagraph2:
              'It intelligently arranges sections, headlines, visuals, and spacing to create professional design files without manual formatting.',
          },
          {
            question: 'What kinds of files can I create with Veeso?',
            answer:
              'You can create presentations, newsletters, social media posts, reports, one-pagers, brochures, and long-form content pages, etc. All are generated from your original text input.',
          },
          {
            question: 'Can I edit the designs after they are generated?',
            answer:
              'Yes. Every output is fully editable. You can adjust text, layout, images, structure, and branding elements directly inside Veeso before exporting.',
          },
          {
            question: 'Is Veeso free to use?',
            answer:
              'Yes. Veeso offers a free plan that includes daily refresh credits so you can explore and create without any upfront cost. You can upgrade to paid plans for more credits and advanced features.',
          },
          {
            question: 'What is a credit?',
            answerParagraph1:
              'Credits are used for all AI-powered features, such as generating new designs, chatting with AI to modify your design, removing backgrounds, and other AI tools.',
            answerParagraph2:
              'Manual editing (like moving text, changing colors, or resizing elements) and exporting your designs do NOT use any credits.',
            answerParagraph3: 'Daily credits refresh every day at midnight UTC.',
          },
          {
            question: 'Can I use my creations for commercial purposes?',
            answerParagraph1: 'Free plan: For personal and non-commercial use only.',
            answerParagraph2:
              'Paid plans: Any work generated is licensed for commercial use, including fonts and media assets.',
          },
        ],
      },
      legal: {
        heading: 'Legal & Privacy',
        privacy: {
          title: 'Privacy Policy',
          description: 'How we handle your data',
        },
        terms: {
          title: 'Terms of Service',
          description: 'User agreements and rules',
        },
        cookie: {
          title: 'Cookie Policy',
          description: 'Our use of cookies',
        },
      },
    },
    getting_started: {
      seo: {
        title: 'Getting Started with Veeso AI | Tutorial & Guide',
        description:
          'Learn how to get started with Veeso AI. Step-by-step guide to creating your first design, understanding credits, and using AI-powered features.',
        keywords:
          'Veeso AI getting started, Veeso tutorial, how to use Veeso, Veeso guide, create design with Veeso',
      },
      title: 'Getting Started',
      description:
        'Learn the basics of Veeso AI and how to create your first design. Get started with our comprehensive guide.',
      content: [
        {
          title: 'What is Veeso AI?',
          body: 'Veeso AI is a content-first design tool that transforms your text and content into polished, professional designs in seconds. Unlike traditional design tools that require design skills, Veeso uses AI to automatically structure your content into visually appealing layouts. Simply paste your text, and Veeso generates complete, editable design files ready for social media, presentations, newsletters, and more.',
        },
        {
          title: 'Creating Your Account',
          body: 'To get started, visit veeso.ai and click "Sign Up" to create your free account. New users receive 300 bonus credits as a welcome gift. You can start creating immediately without any payment information required. The free plan includes daily refresh credits, allowing you to explore Veeso\'s capabilities at no cost.',
        },
        {
          title: 'Understanding Credits',
          body: 'Credits are used for all AI-powered features in Veeso, including generating new designs, chatting with AI to modify designs, removing backgrounds, and other AI tools. Manual editing (moving text, changing colors, resizing elements) and exporting designs do NOT consume credits. Daily credits refresh every day at midnight UTC. You can earn more credits by inviting friends, upgrading your plan, or participating in special events.',
        },
        {
          title: 'Creating Your First Design',
          body: 'To create your first project, navigate to your workspace and click the "Create" button. You can start from a blank canvas or use one of our Magic Templates. Paste your content or text into the input field, select your desired output format (social media post, presentation, newsletter, etc.), and click "Generate". Veeso will analyze your content and create a professional design in seconds. You can then edit, customize, and export your design.',
        },
        {
          title: 'Using Magic Templates',
          body: "Magic Templates are Veeso's intelligent style control system. Instead of fixed templates, Magic Templates dynamically adapt layout structure based on your content length, information hierarchy, tone, and output format. This means the same content can be transformed into different design formats automatically, ensuring optimal visual presentation for each use case.",
        },
        {
          title: 'Editing Your Designs',
          body: 'Every design generated by Veeso is fully editable. You can adjust text, layout, images, structure, and branding elements directly in the editor. Use the AI chat feature to request modifications, or manually edit elements using the intuitive interface. All manual edits are free and do not consume credits.',
        },
        {
          title: 'Exporting Your Designs',
          body: 'Once you\'re satisfied with your design, click the "Export" button. Free plan users can export in standard formats (PNG, JPG, WebP) with a watermark. Paid plan users get HD exports without watermarks and additional formats including PDF, PPT, and HTML. Exporting does not consume credits.',
        },
        {
          title: 'Best Practices',
          body: 'For best results, provide clear, structured content with headings and sections. The more context you give, the better Veeso can understand your intent and create appropriate designs. Use the AI chat feature to refine your designs iteratively. Experiment with different Magic Templates to see how the same content can be presented in various formats.',
        },
      ],
    },
    common: {
      back: 'Back to Help Center',
      not_found: {
        title: 'Category Not Found',
        link: 'Return to Help Center',
      },
      seo: {
        fallback_title: 'Help Center | Veeso AI',
        fallback_description: 'Help Center documentation for Veeso AI',
      },
    },
    troubleshooting: {
      seo: {
        title: 'Troubleshooting & Support | Veeso AI Help',
        description:
          'Need help with Veeso AI? Contact our support team via email or join our Discord community for assistance.',
        keywords: 'Veeso AI support, Veeso help, contact Veeso, Veeso troubleshooting',
      },
      title: 'Troubleshooting',
      description: 'Get help and support for any issues you encounter.',
      discord: {
        title: 'Discord Community',
        description:
          'Join our Discord community for real-time discussions, peer support, and faster response times.',
        cta: 'Join Discord',
      },
      email: {
        title: 'Email Support',
        description:
          'Contact our support team at help@veeso.ai for detailed technical assistance and questions.',
        cta: 'Send Email',
      },
    },
  },
  solutions: {
    index: {
      seo: {
        title: 'Veeso Solutions | AI Design Tools for Content Creator & SMBs',
        description:
          'Discover Veeso AI solutions for content creators, founders, and SMBs. Instantly generate presentations, newsletters, and social visuals from your content.',
        keywords:
          'Veeso solutions, AI design tools, content-first design, creators, SMB teams, presentation generator, newsletter design, social visuals',
      },
      hero: {
        title: 'Solutions for every team',
        subtitle:
          "Discover how Veeso can help your team create stunning visuals. From marketing to HR, we've got you covered.",
      },
      card: {
        learn_more: 'Learn more',
        marketers: {
          title: 'For Marketers',
          description: 'Create stunning marketing content in minutes. No design skills needed.',
        },
        hr: {
          title: 'For Human Resources',
          description:
            'Design engaging HR content, recruitment materials, and internal communications.',
        },
        founders: {
          title: 'For Founders',
          description:
            'Build your brand without a design team. Create professional content for your startup.',
        },
        creators: {
          title: 'For Creators',
          description:
            'Create content that stands out. YouTube thumbnails, Instagram posts, and more.',
        },
        startups: {
          title: 'For Startups',
          description:
            'Scale your design without scaling your team. Create professional materials at startup speed.',
        },
      },
    },
  },
  aiToolsIndex: {
    seo: {
      title: '20+ Best AI Design Tools Library for Marketing & Business',
      description:
        'Access 20+ AI-powered design generators in one platform. Create editable presentations, Amazon A+ layouts, LinkedIn content, posters, brochures, and more.',
      keywords:
        'AI design tools collection, AI layout generator, editable design AI, Instagram post generator, Amazon A+ content generator, LinkedIn post generator, YouTube thumbnail generator, AI brochure maker, AI poster generator, social media design tools, business design tools, production-ready AI design',
    },
    hero: {
      title: 'AI Design Tools Library',
      subtitle: 'Create Editable Design Files, Not Just Images',
    },
    category: {
      social_media: 'Social Media',
      digital_marketing: 'Digital Marketing',
      business_print: 'Business & Print',
      stationery: 'Stationery',
    },
    scroll: {
      left_aria: 'Scroll left',
      right_aria: 'Scroll right',
    },
    card: {
      fallback_title: 'AI Design Tool',
    },
  },
  learn: {
    shell: {
      brand_title: 'Veeso Learn',
      brand_subtitle: 'Product docs for prompts, editing, exports, and real workflows.',
      mobile_menu_open: 'Menu',
      mobile_menu_close: 'Close',
    },
    nav: {
      section: {
        start: 'Get Started',
        create: 'Create',
        ship: 'Ship',
        help: 'Help',
      },
    },
    chapter: {
      welcome: {
        title: 'Welcome',
        heading: 'Welcome to Veeso',
        description:
          'Learn what Veeso does, how it turns content into editable designs, and where to start.',
      },
      quickstart: {
        title: 'Quick Start',
        heading: 'Create Your First Editable AI Design Fast',
        description:
          'Create your first editable AI design with Veeso: choose a format, add content and references, generate, edit text and visuals, then export.',
      },
      prompt: {
        title: 'Prompt Guide',
        heading: 'Write Better Inputs, Get Better Layouts',
        description:
          'Improve Veeso results with better prompts, references, files, and structured content inputs.',
      },
      editor: {
        title: 'Editor Guide',
        heading: 'How the Veeso Editor Works',
        description:
          'Explore the Veeso editor interface, AI chat, pages panel, text editing, image tools, and export workflow.',
      },
      export: {
        title: 'Export',
        heading: 'Export and Deliver Your Work',
        description:
          'Export Veeso projects as PNG, JPG, WebP, PDF, PPT, MP4, SVG, or HTML and fix common export issues.',
      },
      credits: {
        title: 'Credits',
        heading: 'Credits, Billing, and Plans',
        description:
          'Understand Veeso credits, daily free credits, paid credits, generation costs, refunds, and billing behavior.',
      },
      magic_play: {
        title: 'Creative Ideas',
        heading: 'Creative Workflows and Use Cases',
        description:
          'Explore creative workflows and practical use cases for turning ideas and content into Veeso designs.',
      },
      faq: {
        title: 'FAQ',
        heading: 'Frequently Asked Questions',
        description:
          'Find quick answers about Veeso accounts, credits, exports, editing, support, and common product questions.',
      },
      save_sync: {
        title: 'Save and Sync',
        heading: 'How Saving and Sync Work',
        description:
          'Learn how Veeso saves projects, syncs changes, handles version history, and keeps work recoverable.',
      },
      troubleshoot: {
        title: 'Troubleshooting',
        heading: 'Fix Common Issues Fast',
        description:
          'Fix common Veeso issues including blank canvas, stuck generation, failed rendering, export errors, and editing problems.',
      },
      shortcuts: {
        title: 'Shortcuts',
        heading: 'Keyboard Shortcuts',
        description:
          'Use Veeso keyboard shortcuts to edit faster, navigate the canvas, and work more efficiently.',
      },
    },
    search: {
      placeholder: 'Search docs...',
      loading: 'Searching...',
      no_results: 'No results for "{{query}}"',
      footer_hint: '↑↓ move · ↵ open · Esc close',
    },
    markdown: {
      image_placeholder: 'Image placeholder',
      copy_example: 'Copy Example',
      copying: 'Copying...',
      copied: 'Copied',
    },
  },
  cookieBanner: {
    title: 'Cookie Preferences',
    description: 'We use cookies to enhance your experience and keep your data secure.',
    privacy_link: 'Privacy Policy',
    manage_preferences: 'Manage preferences',
    accept_all: 'Accept all',
    modal: {
      title: 'Cookie Preferences',
      description:
        'Manage your cookie settings. You can enable or disable different types of cookies below.',
      essential: {
        label: 'Essential Cookies',
        description: 'Strictly necessary for the website to function.',
      },
      analytics: {
        label: 'Analytics Cookies',
        description: 'Help us understand how you use our website.',
      },
      marketing: {
        label: 'Marketing Cookies',
        description: 'Used to deliver relevant advertisements.',
      },
      save: 'Save preferences',
    },
  },
  legal: {
    terms: {
      seo: {
        title: 'Terms of Service - Veeso AI',
        description:
          'Terms of Service for Veeso AI Platform. Read about our user agreement, acceptable use policy, and service conditions.',
        keywords:
          'Veeso AI terms of service, user agreement, terms and conditions, Veeso AI legal, service terms',
      },
      page: {
        title: 'Terms of Service',
        last_updated: 'Last Updated Date: December 27, 2025',
      },
    },
    privacy: {
      seo: {
        title: 'Privacy Policy - Veeso AI',
        description: 'Learn how Veeso AI collects, uses, and protects your personal information.',
      },
      page: {
        title: 'Privacy Policy',
      },
    },
    cookie: {
      seo: {
        title: 'Cookie Policy - Veeso AI',
        description: 'Learn about how Veeso AI uses cookies and similar technologies.',
      },
      page: {
        title: 'Cookie Policy',
      },
      consent_button: 'Cookie Consent Manager',
    },
  },
};
